// Tutorial slide 4/5. Mock save-word chip that transitions from "+ word" to
// "✓ saved" on tap. Teaches the chat-side library-save flow and the key
// product idea: the AI re-uses words you save, weaving them back into later
// conversations so practice compounds.

import { useState } from 'react';
import { Plus, Check, Library, ChevronRight, Sparkles } from 'lucide-react';
import { C } from '@/styles/tokens';

interface SaveDemoProps {
  onContinue: () => void;
}

const WORDS = [
  { hz: '咖啡', py: 'kāfēi', en: 'coffee' },
  { hz: '高兴', py: 'gāoxìng', en: 'happy' },
  { hz: '认识', py: 'rènshí', en: 'to know' },
];

export function SaveDemo({ onContinue }: SaveDemoProps) {
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const anySaved = Object.values(saved).some(Boolean);

  return (
    <div className="flex-1 flex flex-col">
      <div className="ff-display text-3xl mb-2" style={{ color: C.ink }}>
        Keep what you learn.
      </div>
      <div className="ff-body text-[14px] leading-relaxed mb-8" style={{ color: C.inkLight }}>
        Save a word and your friends will weave it back into later messages. Practice compounds.
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="w-full max-w-[320px]">
          <div className="ff-body text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: C.inkSofter }}>
            小美 · save these
          </div>
          <div
            className="rounded-2xl rounded-bl-sm px-4 py-3 anim-scale-in"
            style={{
              background: C.cream,
              color: C.ink,
              border: `1px solid ${C.mist}`,
              boxShadow: '0 1px 0 rgba(26,22,20,0.04), 0 8px 20px -10px rgba(120,90,70,0.10)',
            }}
          >
            <ul className="flex flex-wrap gap-1.5">
              {WORDS.map((w) => {
                const isSaved = !!saved[w.hz];
                return (
                  <li key={w.hz}>
                    <button
                      type="button"
                      onClick={() => setSaved((prev) => ({ ...prev, [w.hz]: !prev[w.hz] }))}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors"
                      style={{
                        background: isSaved ? C.blush : 'rgba(26,22,20,0.06)',
                        color: isSaved ? C.vermillionDeep : C.ink,
                      }}
                      aria-label={isSaved ? `${w.hz} saved` : `Save ${w.hz}`}
                      aria-pressed={isSaved}
                    >
                      {isSaved ? <Check size={11} aria-hidden /> : <Plus size={11} aria-hidden />}
                      <span className="ff-zh-serif">{w.hz}</span>
                      <span className="opacity-60">{w.py}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {anySaved && (
            <div
              className="mt-3 p-3 rounded-xl flex items-start gap-2 anim-slide-up"
              style={{ background: C.blush, border: `1px solid ${C.coral}` }}
            >
              <Sparkles size={14} className="mt-0.5 shrink-0" style={{ color: C.vermillionDeep }} aria-hidden />
              <div className="ff-body text-[12px] leading-relaxed" style={{ color: C.ink }}>
                Saved words go to your <Library size={11} className="inline align-baseline" aria-hidden />{' '}
                <strong>Library</strong>. Your friends will recognise them and use them around you.
              </div>
            </div>
          )}

          {!anySaved && (
            <div className="ff-body text-[11px] mt-3 italic text-center" style={{ color: C.inkSofter }}>
              Tap any chip to save it.
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onContinue}
        disabled={!anySaved}
        className="mt-6 py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-40"
        style={{ background: C.vermillion }}
      >
        {anySaved ? 'Then practise it' : 'Save at least one word'}
        {anySaved && <ChevronRight size={16} aria-hidden />}
      </button>
    </div>
  );
}
